import { useRouter } from "next/router";
import { ReactNode, useEffect, useMemo, useRef } from "react";

import { useAuthenticationStore } from "@sol/authentication/useAuthenticationStore";
import { Feature, useCheckFeatureFlag, useFeatures } from "@sol/features";
import { interpolate, Route, sanitizeRedirect, useSearchParams } from "@sol/routing";
import { EnumDictionary } from "@sol/utils";

import { isNativeAdminRoute } from "./nativeAdminRouteAccess";

// WIP: Feature sets should be well defined before filling this dictionnary
const ACCESS_CONTROL_LIST: Partial<EnumDictionary<Route, Feature[]>> = {
    [Route.TRAINER_WORKSPACE_BRIEFS]: [Feature.BRIEFS],
    [Route.BRIEFS_DETAILS]: [Feature.BRIEFS],
    [Route.BRIEFS_EDIT]: [Feature.BRIEFS_EDIT],
    [Route.CLASSROOM_BRIEFS_CREATE]: [Feature.BRIEFS_CREATE],

    // CLASSROOMS
    [Route.HOME]: [Feature.HOMEPAGE],
    [Route.CLASSROOM_TEMPLATE]: [Feature.USE_CLASSROOM_TEMPLATES],
    [Route.CLASSROOMS]: [Feature.CLASSROOM],
    [Route.CLASSROOMS_CREATE]: [Feature.CLASSROOM_CREATE],
    [Route.CLASSROOMS_EDIT]: [Feature.CLASSROOM_UPDATE],
    [Route.CLASSROOMS_LEARNERS]: [Feature.CLASSROOM_UPDATE_LEARNERS],
    [Route.CLASSROOM_LEARNER_PROGRESSION]: [Feature.CLASSROOM_LEARNER_PROGRESSION],

    // BRIEFS
    [Route.CLASSROOM_BRIEFS]: [Feature.CLASSROOM_BRIEFS],
    [Route.BRIEFS_COLLABORATE]: [Feature.BRIEFS_CREATE], // TODO: Désactiver l'onglet dans trainer-workspace/brief
    [Route.BRIEFS_EXPLORE]: [Feature.BRIEFS_EXPLORE, Feature.MENU_BRIEFS_EXPLORER], // TODO: Désactiver l'onglet dans classrooms/<uuid>/briefs
    [Route.ADMIN]: [Feature.ADMIN],

    [Route.BRIEFS_CREATE]: [Feature.BRIEFS_CREATE],
    // TODO: think a new permission system for scoped route access (ex: ErrorBoundary + restricted scope)
    // or we don't care as the page is not reachable through user experience (no such a link is displayed)
    [Route.CLASSROOM_BRIEFS_EDIT]: [Feature.BRIEFS_CREATE],
    [Route.CLASSROOM_BRIEFS_DETAILS]: [Feature.BRIEFS],
    [Route.OLD_CLASSROOM_BRIEFS_DETAILS]: [Feature.BRIEFS],
    [Route.CLASSROOM_BRIEFS_ASSIGN]: [Feature.BRIEFS_CREATE],
    [Route.CLASSROOM_BRIEFS_GROUP_ASSIGN]: [Feature.BRIEFS_CREATE],

    // USER
    [Route.PROFILE]: [Feature.PROFILE],
    [Route.PROFILE_EDIT]: [Feature.PROFILE],
    [Route.ANNOUNCEMENTS]: [Feature.PROFILE],

    // PUBLIC
    [Route.COOKIE_POLICY]: [],
    [Route.COOKIE_PREFERENCES]: [],
    [Route.PAGE]: [],
    [Route.NOTIFICATION_CALLBACK]: [Feature.NOTIFICATIONS],

    // ANONYMOUS
    [Route.LOGIN]: [Feature.LOGIN],
    [Route.RESET_PASSWORD]: [Feature.LOGIN],
    [Route.REGISTRATION]: [Feature.REGISTER],

    [Route.CLASSROOM_WORKSPACES]: [Feature.TOPICS, Feature.WORKSPACE_GROUP],
    [Route.CLASSROOM_WORKSPACES_REFLECTIVE_ANALYSIS_DETAILS]: [Feature.TOPICS, Feature.WORKSPACE_GROUP],

    // FOLLOW UPS
    [Route.FOLLOW_UPS_LIST]: [Feature.FOLLOW_UPS],
    [Route.FOLLOW_UPS_CREATE]: [Feature.FOLLOW_UPS_CREATE],
    [Route.FOLLOW_UPS_DETAILS]: [Feature.FOLLOW_UPS],
    [Route.FOLLOW_UPS_EDIT]: [Feature.FOLLOW_UPS_EDIT],

    // RESOURCES
    [Route.CLASSROOM_RESOURCES]: [Feature.RESOURCES],

    // PROFESSIONAL SITUATIONS
    [Route.TRAINER_WORKSPACE_PROFESSIONAL_SITUATION_CREATE]: [Feature.PROFESSIONAL_SITUATION_CREATE],
    [Route.TRAINER_WORKSPACE_PROFESSIONAL_SITUATION_EDIT]: [Feature.PROFESSIONAL_SITUATION_UPDATE],
    [Route.TRAINER_WORKSPACE_PROFESSIONAL_SITUATION]: [Feature.PROFESSIONAL_SITUATION],

    // MISSIONS
    [Route.MISSION_CREATE]: [Feature.MISSION_CREATE],

    // FRAMEWORKS
    [Route.TRAINER_WORKSPACE_FRAMEWORKS]: [Feature.FRAMEWORKS],
    [Route.FRAMEWORKS]: [Feature.FRAMEWORKS_ACCESS_TUTOR],

    // Agenda
    [Route.CLASSROOM_AGENDA]: [Feature.AGENDA],
    [Route.CLASSROOM_AGENDA_SCHEDULE]: [Feature.PROFESSIONAL_SITUATION],

    // Classroom Dashboard
    [Route.CLASSROOM_DASHBOARD]: [Feature.MENU_CLASSROOM_DASHBOARD],
    [Route.CLASSROOMS_STATISTICS]: [Feature.CLASSROOM_STATISTICS],

    // WORKSPACES
    [Route.MY_WORKSPACE]: [Feature.MY_WORKSPACE],
    [Route.MY_WORKSPACE_ASSIGNMENT]: [Feature.MY_WORKSPACE],
    [Route.WORKSPACES]: [Feature.WORKSPACES_ACCESS_TUTOR],
    [Route.WORKSPACES_REFLECTIVE_ANALYSIS_DETAILS]: [Feature.WORKSPACES_ACCESS_TUTOR],

    // LEARNERS
    [Route.LEARNERS]: [Feature.LEARNERS_CARD_ACCESS_TUTOR],
};

type Props = { children: ReactNode };

export const RouteAccessGuard = ({ children }: Props) => {
    const { token } = useAuthenticationStore();
    const features = useFeatures();
    const checkFeatureFlag = useCheckFeatureFlag();
    const router = useRouter();
    const { route, replace, asPath } = router;

    const redirect = sanitizeRedirect(useSearchParams().redirect);

    const redirectRef = useRef<string | null>(null);

    const defaultPage = useMemo(() => {
        if (checkFeatureFlag(Feature.HOMEPAGE)) {
            return Route.HOME;
        } else if (checkFeatureFlag(Feature.ADMIN)) {
            return Route.ADMIN;
        } else if (checkFeatureFlag(Feature.PROFILE)) {
            return Route.PROFILE;
        }

        return interpolate(Route.LOGIN, { redirect: asPath });
    }, [checkFeatureFlag, asPath]);

    const allow = useMemo(() => {
        if (isNativeAdminRoute(route as Route)) {
            return !!token;
        }

        const acl: Feature[] = ACCESS_CONTROL_LIST[route as Route] ?? [];

        // Allow by default
        if (acl.length === 0) {
            return true;
        }

        // Allow only if one of our features match acl
        return acl.findIndex(feature => features.has(feature)) > -1;
    }, [route, features, token]);

    // Redirects run in an effect, not in render: calling replace() directly in the render body
    // re-fires on every re-render while `allow` stays false (e.g. RBAC features still loading
    // after login), which trips React's "Too many re-renders" guard (SIMPLONLINE-UI-1P7 and co).
    useEffect(() => {
        if (redirectRef.current) {
            // Second pass: we're now on the page we redirected to (e.g. login), and the original
            // target was stashed below. Force a hard navigation (full origin) so it isn't treated
            // as a same-route no-op by the router.
            const pendingRedirect = redirectRef.current;
            redirectRef.current = null;
            replace(`${window.location.origin}${pendingRedirect}`);
            return;
        }

        if (allow) {
            return;
        }

        // Stash the originally requested page (if any) to restore after the bounce below resolves.
        redirectRef.current = redirect ?? null;
        replace(defaultPage);
    }, [allow, defaultPage, redirect, replace, route, asPath]);

    if (allow) {
        return <>{children}</>;
    }

    return null;
};

export default RouteAccessGuard;
